import type { MaybeRef } from '@vueuse/core';
import { get } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { ref, watch } from 'vue';
import { MarketPage, SportType } from '@/interface/enum';
import { deepCompareComputed } from '@/libraries/composables/deepCompareComputed';
import BusinessLogicHelper from '@/libraries/helpers/businessLogicHelper';
import { getFilter } from '@/libraries/oddsApi/helpers';
import { useOddsApiStore } from '@/stores/oddsApiStore';
import { useRootStore } from '@/stores/rootStore';
import { EnumEventDateFilterType, EnumOddsCategoryType, EnumPresetFilterType, EnumTimeZoneType, useEventsCountInfoQuery as _useEventsCountInfoQuery } from '@/types';

interface IUseEventsCountInfoQuery {
    sportType: MaybeRef<SportType>;
    isMixParlayMode: MaybeRef<boolean>;
    isIncludeLiveInToday?: MaybeRef<boolean | undefined>;
    tournamentIds?: MaybeRef<number[] | undefined>;
    tournamentNames?: MaybeRef<string[] | undefined>;
}

interface IEventsCountInfo {
    [MarketPage.Live]: number;
    [MarketPage.Today]: number;
    [MarketPage.EarlyMarket]: number;
    [MarketPage.EarlyMarketPlus1]: number;
    [MarketPage.EarlyMarketPlus2]: number;
    [MarketPage.EarlyMarketPlus3]: number;
    [MarketPage.EarlyMarketPlus4Over]: number;
    [MarketPage.Outright]?: number;
}

const marketPages = [
    MarketPage.Live,
    MarketPage.Today,
    MarketPage.EarlyMarket,
    MarketPage.EarlyMarketPlus1,
    MarketPage.EarlyMarketPlus2,
    MarketPage.EarlyMarketPlus3,
    MarketPage.EarlyMarketPlus4Over,
    MarketPage.Outright,
];

const getSingleDefaultEventsCountInfo = () => ({
    [MarketPage.Live]: 0,
    [MarketPage.Today]: 0,
    [MarketPage.EarlyMarket]: 0,
    [MarketPage.EarlyMarketPlus1]: 0,
    [MarketPage.EarlyMarketPlus2]: 0,
    [MarketPage.EarlyMarketPlus3]: 0,
    [MarketPage.EarlyMarketPlus4Over]: 0,
    [MarketPage.Outright]: 0,
});

const getMixParlayDefaultEventsCountInfo = () => ({
    [MarketPage.Live]: 0,
    [MarketPage.Today]: 0,
    [MarketPage.EarlyMarket]: 0,
    [MarketPage.EarlyMarketPlus1]: 0,
    [MarketPage.EarlyMarketPlus2]: 0,
    [MarketPage.EarlyMarketPlus3]: 0,
    [MarketPage.EarlyMarketPlus4Over]: 0,
});

function getDefaultEventsCountInfo(isMixParlayMode: boolean) {
    return isMixParlayMode ? getMixParlayDefaultEventsCountInfo() : getSingleDefaultEventsCountInfo();
}

export function useEventsCountInfoQuery({
    sportType,
    isMixParlayMode,
    isIncludeLiveInToday = false,
    tournamentIds = undefined,
    tournamentNames = undefined,
}: IUseEventsCountInfoQuery) {
    const eventsCountInfo = ref<IEventsCountInfo>(getDefaultEventsCountInfo(get(isMixParlayMode)));

    const variable = deepCompareComputed(() => ({
        query: {
            sport: SportType.toOddsApi(get(sportType)),
            oddsCategory: get(isMixParlayMode) ? EnumOddsCategoryType.MixParlay : EnumOddsCategoryType.All,
            filters: [
                getFilter(MarketPage.Live),
                get(isIncludeLiveInToday) ? { presetFilter: EnumPresetFilterType.All, date: EnumEventDateFilterType.TodayExpanded } : getFilter(MarketPage.Today),
                getFilter(MarketPage.EarlyMarket),
                getFilter(MarketPage.EarlyMarketPlus1),
                getFilter(MarketPage.EarlyMarketPlus2),
                getFilter(MarketPage.EarlyMarketPlus3),
                getFilter(MarketPage.EarlyMarketPlus4Over),
                ...(get(isMixParlayMode) ? [] : [getFilter(MarketPage.Outright)]),
            ],
            tournamentIds: get(tournamentIds),
            tournamentNames: get(tournamentNames),
            timeZone: EnumTimeZoneType.UTC_8,
        },
    }));

    const { customerSettings } = storeToRefs(useOddsApiStore());
    const { setMarketPageEventCount } = useRootStore();

    const { onResult, refetch, loaded } = _useEventsCountInfoQuery(
        variable,
        { pollInterval: 30000 },
    );

    onResult((result) => {
        if (!result) {
            return;
        }

        eventsCountInfo.value = result.reduce((acc, curr, index) => {
            const key = marketPages[index];
            if (key in acc) {
                acc[key as keyof typeof acc] = BusinessLogicHelper.filterEventCount(
                    curr,
                    customerSettings.value,
                    SportType.toOddsApi(get(sportType)),
                );
            }
            return acc;
        }, getDefaultEventsCountInfo(get(isMixParlayMode)));

        setMarketPageEventCount(eventsCountInfo.value);
    });

    watch(variable, () => {
        eventsCountInfo.value = getDefaultEventsCountInfo(get(isMixParlayMode));
        setMarketPageEventCount(eventsCountInfo.value);
    });

    return {
        eventsCountInfo,
        loaded,
        refetch,
    };
}
